<template>
  <transition appear name="fade">
    <!-- MOBILE / TABLET VIEW -->
    <div class="pd-root" v-if="isMobileOrTablet">
      <appHeader></appHeader>
      <div class="pd-mobile-wrap">
        <div v-if="product" class="pd-mobile-inner">

          <!-- Unified white card: image + content in one -->
          <div class="pd-unified-card">

            <!-- Image area -->
            <div class="pd-img-area">
              <img :alt="product.name" :src="product.imageUrl" class="pd-hero-img" />
              <!-- Best Buy badge: top-right corner of image -->
              <div v-if="product.supplierName == 'Best Buy'" class="pd-bb-badge">
                <img src="@/assets/images/best-buy-logo.png" alt="Best Buy Logo" class="pd-bb-badge-img" />
                <span class="pd-bb-badge-text">Fulfilled by Best Buy</span>
              </div>
            </div>

            <!-- Divider -->
            <div class="pd-card-divider"></div>

            <!-- Content section -->
            <div class="pd-content-card">
              <div class="pd-breadcrumb-row">
                <appBreadcrumb></appBreadcrumb>
              </div>

              <h1 class="pd-product-name">{{ product.name }}</h1>

              <div v-if="currentProductVersion" class="pd-price-row">
                <span class="pd-price-amount">{{ currentProductVersionTotalPoints | numToLocale }}</span>
                <span class="pd-price-unit">pts</span>
              </div>

            <form v-on:submit.prevent="" class="pd-form">
              <!-- Version Selector -->
              <div v-if="product.versions.length > 0" class="pd-versions-wrap">
                <p class="pd-label">{{ currentProductVersion.specName }}</p>
                <div class="pd-version-chips">
                  <template v-for="version in product.versions">
                    <label
                      v-if="version.specValue != null"
                      :key="version.id"
                      :class="['pd-chip', { 'pd-chip--active': version.id === currentProductVersion.id }]"
                    >
                      <input :value="version.id" type="radio" v-on:click="chooseVersion(version.id)" class="pd-chip-input" />
                      {{ version.specValue }}
                    </label>
                  </template>
                </div>
              </div>

              <!-- Quantity -->
              <div class="pd-qty-row">
                <p class="pd-label">Quantity</p>
                <b-form-select v-model="quantitySelected" :options="quantityOptions" class="pd-qty-select"></b-form-select>
              </div>

              <!-- CTA Buttons -->
              <div class="pd-cta-row">
                <button :disabled="isAddProcessing" class="pd-btn pd-btn--primary" v-on:click="addToCart">
                  <font-awesome-icon icon="shopping-cart" class="pd-btn-icon" />
                  Add to Cart
                </button>
                <button class="pd-btn pd-btn--ghost" v-on:click="addToWishlist">
                  <font-awesome-layers class="fa pd-btn-icon">
                    <font-awesome-icon icon="clipboard-list" />
                    <font-awesome-icon class="fa-inverse" icon="heart" transform="shrink-3 down-5 right-8" />
                    <font-awesome-icon icon="heart" transform="shrink-7 down-5 right-8" />
                  </font-awesome-layers>
                  {{ wishlistStatus }}
                </button>
              </div>
            </form>

            <!-- Notice Banner -->
            <div class="pd-notice">
              <font-awesome-icon icon="info-circle" class="pd-notice-icon" />
              <p class="pd-notice-text">Points are subject to change based on availability and vendor fulfillment. The latest point value will be verified when displayed in cart.</p>
            </div>
          </div><!-- /.pd-content-card -->
          </div><!-- /.pd-unified-card -->

          <!-- Info Sections -->
          <div class="pd-sections">
            <div class="pd-section" v-if="product.description">
              <h2 class="pd-section-title">Product Information</h2>
              <div class="pd-section-body" v-html="product.description"></div>
            </div>
            <div v-if="product.terms != null" class="pd-section">
              <h2 class="pd-section-title">Terms &amp; Conditions</h2>
              <div class="pd-section-body" v-html="product.terms"></div>
            </div>
          </div>

        </div>
      </div>

      <!-- Success Modal -->
      <b-modal id="modal-added" size="md" modal-class="pd-modal">
        <template #modal-header="{ close }">
          <div class="pd-modal-header">
            <span class="pd-modal-title"><font-awesome-icon icon="check" class="pd-modal-check" /> Added to Cart</span>
            <button class="pd-modal-close" @click="close()">
              <img src="../../assets/icons/close.svg" />
            </button>
          </div>
        </template>
        <b-container fluid class="pd-modal-body-inner" :style="{ padding: '15px !important' }">
          <img :alt="product.name" :src="product.imageUrl" class="pd-modal-img" />
          <div class="pd-modal-info">
            <h6 class="pd-modal-name">{{ product.name }}</h6>
            <p v-if="currentProductVersion" class="pd-modal-price">
              <strong>{{ currentProductVersion.points | numToLocale }}</strong> <span>pts</span>
            </p>
          </div>
        </b-container>
        <template #modal-footer>
          <router-link class="pd-btn pd-btn--primary w-100 mb-2 mt-2" :to="{ name: 'Cart' }">
            <font-awesome-icon icon="shopping-cart" class="pd-btn-icon" /> View Cart
          </router-link>
          <button class="pd-btn pd-btn--ghost w-100" @click="hideModal">
            <font-awesome-icon icon="angle-left" class="pd-btn-icon" /> Continue Shopping
          </button>
        </template>
      </b-modal>

      <appFooter></appFooter>
      <navigationFooter></navigationFooter>
    </div>

    <!-- DESKTOP VIEW -->
    <div v-else class="pd-root pd-desktop-root">
      <appHeader></appHeader>
      <div class="pd-desktop-layout">
        <aside class="pd-sidebar">
          <appProductSidebar></appProductSidebar>
        </aside>

        <main v-if="product" class="pd-desktop-main">
          <!-- Two column: image + details -->
          <div class="pd-desktop-top">
            <!-- Image Column -->
            <div class="pd-desktop-img-col">
              <div class="pd-desktop-img-wrap">
                <!-- Best Buy badge: top-right corner of image -->
                <div v-if="product.supplierName == 'Best Buy'" class="pd-bb-badge">
                  <img src="@/assets/images/best-buy-logo.png" alt="Best Buy Logo" class="pd-bb-badge-img" />
                  <span class="pd-bb-badge-text">Fulfilled by Best Buy</span>
                </div>
                <label for="pd-zoom-toggle" class="pd-zoom-trigger">
                  <img :alt="product.name" :src="product.imageUrl" class="pd-desktop-img" />
                  <span class="pd-zoom-icon">🔍</span>
                </label>
                <input type="checkbox" id="pd-zoom-toggle" class="pd-zoom-checkbox" />
                <label for="pd-zoom-toggle" class="pd-zoom-overlay">
                  <img :alt="product.name" :src="product.imageUrl" class="pd-zoom-full-img" />
                </label>
              </div>
              <p class="pd-zoom-hint"><span class="pd-zoom-hint-icon">🔍</span> Click image to zoom</p>
            </div>

            <!-- Details Column -->
            <div class="pd-desktop-details">
              <div class="pd-breadcrumb-row">
                <appBreadcrumb></appBreadcrumb>
              </div>

              <h1 class="pd-product-name pd-product-name--desktop">{{ product.name }}</h1>

              <div v-if="currentProductVersion" class="pd-price-row pd-price-row--desktop">
                <span class="pd-price-amount pd-price-amount--desktop">{{ currentProductVersionTotalPoints | numToLocale }}</span>
                <span class="pd-price-unit pd-price-unit--desktop">points</span>
              </div>

              <form v-on:submit.prevent="" class="pd-form">
                <!-- Version Selector -->
                <div
                  v-if="product.versions.length > 1 || (product.versions.length > 0 && product.versions[0].specValue != null)"
                  class="pd-versions-wrap"
                >
                  <p class="pd-label">{{ currentProductVersion.specName }}</p>
                  <div class="pd-version-chips">
                    <template v-for="version in product.versions">
                      <label
                        v-if="version.specValue != null"
                        :key="version.id"
                        :class="['pd-chip', { 'pd-chip--active': version.id === currentProductVersion.id }]"
                      >
                        <input :value="version.id" type="radio" v-on:click="chooseVersion(version.id)" class="pd-chip-input" />
                        {{ version.specValue }}
                      </label>
                    </template>
                  </div>
                </div>

                <!-- Quantity -->
                <div class="pd-qty-row">
                  <p class="pd-label">Quantity</p>
                  <b-form-select v-model="quantitySelected" :options="quantityOptions" class="pd-qty-select"></b-form-select>
                </div>

                <!-- CTA Buttons -->
                <div class="pd-cta-row pd-cta-row--desktop">
                  <button class="pd-btn pd-btn--primary" :disabled="isAddProcessing" v-on:click="addToCart">
                    <font-awesome-icon icon="shopping-cart" class="pd-btn-icon" />
                    Add to Cart
                  </button>
                  <button class="pd-btn pd-btn--ghost" v-on:click="addToWishlist">
                    <font-awesome-layers class="fa pd-btn-icon">
                      <font-awesome-icon icon="clipboard-list" />
                      <font-awesome-icon class="fa-inverse" icon="heart" transform="shrink-3 down-5 right-8" />
                      <font-awesome-icon icon="heart" transform="shrink-7 down-5 right-8" />
                    </font-awesome-layers>
                    {{ wishlistStatus }}
                  </button>
                </div>
              </form>

              <!-- Notice Banner -->
              <div class="pd-notice mt-2">
                <font-awesome-icon icon="info-circle" class="pd-notice-icon" />
                <p class="pd-notice-text">Points are subject to change based on availability and vendor fulfillment. The latest point value will be verified when displayed in cart.</p>
              </div>
            </div>
          </div>

          <!-- Info Sections -->
          <div class="pd-sections pd-sections--desktop">
            <div class="pd-section" v-if="product.description">
              <h2 class="pd-section-title">Product Information</h2>
              <div class="pd-section-body" v-html="product.description"></div>
            </div>
            <div v-if="product.terms != null" class="pd-section">
              <h2 class="pd-section-title">Terms &amp; Conditions</h2>
              <div class="pd-section-body" v-html="product.terms"></div>
            </div>
          </div>
        </main>

        <!-- Success Modal -->
        <b-modal id="modal-added" size="md" modal-class="pd-modal">
          <template #modal-header="{ close }">
            <div class="pd-modal-header">
              <span class="pd-modal-title"><font-awesome-icon icon="check" class="pd-modal-check" /> Added to Cart</span>
              <button class="pd-modal-close" @click="close()">
                <img src="../../assets/icons/close.svg" />
              </button>
            </div>
          </template>
          <b-container fluid class="pd-modal-body-inner">
            <img :alt="product.name" :src="product.imageUrl" class="pd-modal-img" />
            <div class="pd-modal-info">
              <h6 class="pd-modal-name">{{ product.name }}</h6>
              <p v-if="currentProductVersion" class="pd-modal-price">
                <strong>{{ currentProductVersion.points | numToLocale }}</strong> <span>pts</span>
              </p>
            </div>
          </b-container>
          <template #modal-footer>
            <router-link class="pd-btn pd-btn--primary w-100 mb-2 mt-2" :to="{ name: 'Cart' }">
              <font-awesome-icon icon="shopping-cart" class="pd-btn-icon" /> View Cart
            </router-link>
            <button class="pd-btn pd-btn--ghost w-100" @click="hideModal">
              <font-awesome-icon icon="angle-left" class="pd-btn-icon" /> Continue Shopping
            </button>
          </template>
        </b-modal>
      </div>

      <appFooter></appFooter>
    </div>
  </transition>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import WhatsNewBar from "@/components/WhatsNewBar.vue";
import ProductSidebar from "./ProductSidebar.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import navigationFooter from "../../components/navigationFooter.vue";
import { isMobile, isTablet } from "mobile-device-detect";
import { mapState } from "vuex";

export default {
  props: {
    productId: Number,
    productCategoryId: Number,
  },
  watch: {
    productId: function (newValue, oldValue) {
      this.productId = newValue;
      this.$store.dispatch("product/fetchProductDetails", {
        categoryId: this.productCategoryId,
        productId: this.productId,
      });
    },
    product: function (newValue, oldValue) {
      this.chooseVersion(newValue.versions[0].id);
    },
  },
  data() {
    return {
      wishlistStatus: "Add to wishlist",
      chosenProductVersion: undefined,
      quantitySelected: 1,
      quantityOptions: [
        { value: 1, text: "1" },
        { value: 2, text: "2" },
        { value: 3, text: "3" },
      ],
      dismissSecs: 5,
      dismissCountDown: 0,
      isAddProcessing: false,
    };
  },
  computed: {
    ...mapState({
      grossUpTaxes: (state) => state.company.grossUpTaxes,
      currentCategoryId: (state) => state.category.currentCategoryId,
      product: (state) => state.product.details,
      defaultProductVersion: (state) => state.product.details.versions[0],
      wishlistItems: (state) => state.wishlist.items,
    }),
    isMobileOrTablet: function () {
      return isMobile || isTablet;
    },
    currentProductVersion: function () {
      return this.chosenProductVersion
        ? this.chosenProductVersion
        : this.defaultProductVersion;
    },
    currentProductVersionTotalPoints: function () {
      if (this.grossUpTaxes) {
        return (
          this.currentProductVersion.points +
          this.currentProductVersion.taxPoints
        );
      } else {
        return this.currentProductVersion.points;
      }
    },
  },
  mounted() {
    this.scrollToTop();
  },
  methods: {
    hideModal() {
      this.$bvModal.hide("modal-added");
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    chooseVersion: function (versionId) {
      this.chosenProductVersion = this.product.versions.find(
        (item) => item.id === versionId
      );
    },
    addToCart: async function () {
      if (this.isAddProcessing == false) {
        this.isAddProcessing = true;
        await this.$store
          .dispatch("cart/addCartItem", {
            productVersionId: this.currentProductVersion.id,
            quantity: this.quantitySelected,
            categoryID: this.productCategoryId,
          })
          .then(() => {
            this.showAdded("Item added to cart", () => {});
          })
          .catch(() => {
            this.showError("Failed to add item to cart");
          })
          .finally(() => {
            this.isAddProcessing = false;
          });
      } else {
        console.warn("already processing cart item");
      }
    },
    addToWishlist: function () {
      this.$store
        .dispatch("wishlist/addWishlistItem", {productVersionId: this.currentProductVersion.id, categoryID: this.productCategoryId})
        .then(
          () => {
            this.showSuccess("Item added to wishlist", () => {});
          },
          (err) => {
            if (err.response.status === 409) {
              // already in wishlist
            } else {
              this.showError("Failed to add item to wishlist");
            }
          }
        )
        .catch(() => {
          this.showError("Failed to add item to wishlist");
        });
    },
    showAdded: function (message, onClose) {
      this.$bvModal.show("modal-added");
    },
    showSuccess: function (message, onClose) {
      this.$bvModal
        .msgBoxOk(message, {
          size: "sm",
          headerClass: "p-2 border-bottom-0",
          footerClass: "p-2 border-top-0",
          centered: true,
        })
        .then(() => {
          onClose();
        });
    },
    showError: function (message) {
      this.$bvModal.msgBoxOk(message, {
        size: "sm",
        headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0",
        okVariant: "danger",
        centered: true,
      });
    },
  },
  components: {
    appHeader: Header,
    appFooter: Footer,
    appWhatsNewBar: WhatsNewBar,
    appProductSidebar: ProductSidebar,
    appBreadcrumb: Breadcrumb,
    navigationFooter: navigationFooter,
  },
  created() {
    this.$store.dispatch("wishlist/fetchWishlist");
    if (this.productId && this.productCategoryId) {
      this.$store.dispatch("product/fetchProductDetails", {
        categoryId: this.productCategoryId,
        productId: this.productId,
      });
    }
    console.log(this.product);
  },
};
</script>

<style lang="scss" scoped>
/* ─────────────────────────────────────────────
   FADE TRANSITION
───────────────────────────────────────────── */
.fade-enter-active,
.fade-leave-active { transition: opacity 0.35s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ─────────────────────────────────────────────
   ROOT
───────────────────────────────────────────── */
.pd-root {
  background: #f7f8fa;
  min-height: 100vh;
}
.pd-desktop-root {
  padding-top: 64px;
}

/* ─────────────────────────────────────────────
   DESKTOP LAYOUT
───────────────────────────────────────────── */
.pd-desktop-layout {
  display: flex;
  align-items: flex-start;
  max-width: 1440px;
  margin: 0 auto;
  padding: 28px 24px 60px;
  padding-top: unset;
  gap: 28px;
}

.pd-sidebar {
  flex: 0 0 260px;
  position: sticky;
  top: 80px;
}

.pd-desktop-main {
  flex: 1;
  min-width: 0;
}

/* ─────────────────────────────────────────────
   DESKTOP TOP: image + details side-by-side
───────────────────────────────────────────── */
.pd-desktop-top {
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 40px;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 8px 24px rgba(0,0,0,0.05);
  margin-bottom: 24px;
}

/* ─────────────────────────────────────────────
   DESKTOP IMAGE
───────────────────────────────────────────── */
.pd-desktop-img-col {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pd-desktop-img-wrap {
  position: relative; // needed for badge absolute positioning
  width: 100%;
  border-radius: 12px;
  overflow: visible; // FIX: was 'hidden' which clipped the image
  background: #ffffff;
  border: 1px solid #e8eaed;
  transition: box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.12);
  }
}

.pd-desktop-img {
  width: 100%;        // FIX: fill the container width
  height: auto;       // FIX: let height be natural, no clipping
  max-height: 420px;  // FIX: cap height so it doesn't go too tall
  object-fit: contain;
  display: block;
  padding: 16px;
  border-radius: 12px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.03);
  }
}

.pd-zoom-hint {
  margin-top: 10px;
  font-size: 0.75rem;
  color: #9aa0a6;
  letter-spacing: 0.02em;
}

/* ─────────────────────────────────────────────
   BEST BUY BADGE — top-right corner of image
   (shared by both mobile & desktop)
───────────────────────────────────────────── */
.pd-bb-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  padding: 4px 8px 4px 5px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.12);
  pointer-events: none; // don't block image click-to-zoom
}

.pd-bb-badge-img {
  height: 18px;
  width: auto;
  object-fit: contain;
  display: block;
}

.pd-bb-badge-text {
  font-size: 0.7rem;
  font-weight: 600;
  color: #3c4043;
  white-space: nowrap;
  line-height: 1;
}

/* Mobile-only: slightly more top padding inside the image area */
.pd-mobile-wrap .pd-bb-badge {
  top: 8px;
  right: 8px;
  padding: 3px 7px 3px 4px; // slightly more compact on mobile
}

/* ─────────────────────────────────────────────
   DESKTOP DETAILS
───────────────────────────────────────────── */
.pd-desktop-details {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: 4px;
}

/* ─────────────────────────────────────────────
   BREADCRUMB
───────────────────────────────────────────── */
.pd-breadcrumb-row {
  margin-bottom: 12px;

  :deep(.breadcrumb) {
    background: transparent;
    padding: 0;
    margin: 0;
    font-size: 0.8rem;
  }
}

/* ─────────────────────────────────────────────
   PRODUCT NAME
───────────────────────────────────────────── */
.pd-product-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.35;
  margin: 0 0 4px; // tightened — less gap between name and accent line
  letter-spacing: -0.01em;

  &::after {
    content: '';
    display: block;
    width: 28px;
    height: 3px;
    background: $primary;
    border-radius: 2px;
    margin-top: 6px; // tightened — less empty space below title
    margin-bottom: 8px; // small gap before price
  }

  &--desktop {
    font-size: 1.7rem;
  }
}

/* ─────────────────────────────────────────────
   PRICE
───────────────────────────────────────────── */
.pd-price-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 0; // ::after on pd-product-name already provides bottom gap
  margin-bottom: 16px;

  &--desktop {
    margin-bottom: 24px;
  }
}

.pd-price-amount {
  font-size: 1.6rem;
  font-weight: 800;
  color: $primary;
  letter-spacing: -0.02em;

  &--desktop {
    font-size: 2.1rem;
  }
}

.pd-price-unit {
  font-size: 0.85rem;
  font-weight: 600;
  color: #80868b;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &--desktop {
    font-size: 0.95rem;
  }
}

/* ─────────────────────────────────────────────
   FORM
───────────────────────────────────────────── */
.pd-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ─────────────────────────────────────────────
   LABEL
───────────────────────────────────────────── */
.pd-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #80868b;
  margin: 0 0 8px;
}

/* ─────────────────────────────────────────────
   VERSION CHIPS
───────────────────────────────────────────── */
.pd-version-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pd-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 16px;
  border-radius: 20px;
  border: 1.5px solid #dadce0;
  background: #ffffff;
  font-size: 0.85rem;
  font-weight: 500;
  color: #3c4043;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  line-height: 1;

  &:hover {
    border-color: $primary;
    color: $primary;
    background: rgba($primary, 0.04);
  }

  &--active {
    border-color: $primary;
    background: $primary;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba($primary, 0.3);
  }
}

.pd-chip-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

/* ─────────────────────────────────────────────
   QUANTITY
───────────────────────────────────────────── */
.pd-qty-select {
  max-width: 100px;
  height: 40px;
  border-radius: 8px;
  border: 1.5px solid #dadce0;
  font-size: 0.9rem;
  padding: 0 12px;
  color: #3c4043;
  background-color: #fff;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    border-color: $primary;
    outline: none;
    box-shadow: 0 0 0 3px rgba($primary, 0.15);
  }
}

/* ─────────────────────────────────────────────
   CTA BUTTONS
───────────────────────────────────────────── */
.pd-cta-row {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &--desktop {
    flex-direction: row;
    gap: 12px;
  }
}

.pd-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 24px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.18s ease;
  text-decoration: none;
  white-space: nowrap;

  &--primary {
    background: $primary;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba($primary, 0.35);

    &:hover:not(:disabled) {
      color: #fff;
      background: darken($primary, 6%);
      box-shadow: 0 4px 16px rgba($primary, 0.4);
      transform: translateY(-1px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 1px 4px rgba($primary, 0.3);
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  &--ghost {
    background: #ffffff;
    color: #3c4043;
    border: 1.5px solid #dadce0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.07);

    &:hover {
      border-color: $primary;
      color: $primary;
      background: rgba($primary, 0.04);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

.pd-btn-icon {
  font-size: 0.9em;
}

/* ─────────────────────────────────────────────
   NOTICE BANNER
───────────────────────────────────────────── */
.pd-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff8e6;
  border: 1px solid #ffd54f;
  border-left: 4px solid #ffb300;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 4px;
}

.pd-notice-icon {
  color: #e65100;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.pd-notice-text {
  font-size: 0.8rem;
  color: #5f4000;
  margin: 0;
  line-height: 1.5;
}

/* ─────────────────────────────────────────────
   PRODUCT INFO & TERMS SECTIONS
───────────────────────────────────────────── */
.pd-sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 12px 16px;

  &--desktop {
    padding: 0;
  }
}

.pd-section {
  background: #ffffff;
  border-radius: 14px;
  padding: 24px 28px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
}

.pd-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #80868b;
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f2f5;
}

.pd-section-body {
  font-size: 0.9rem;
  line-height: 1.7;
  color: #3c4043;

  :deep(p) { margin-bottom: 0.75em; }
  :deep(ul), :deep(ol) { padding-left: 1.4em; }
  :deep(a) { color: $primary; }

  .pd-sections--desktop & {
    columns: 2;
    column-gap: 40px;
  }
}

/* ─────────────────────────────────────────────
   MOBILE LAYOUT
───────────────────────────────────────────── */
.pd-mobile-wrap {
  padding-top: 20px;
  padding-bottom: 80px;
  background: #f0f2f5;
}

.pd-mobile-inner {
  display: flex;
  flex-direction: column;
}

/* ─────────────────────────────────────────────
   MOBILE: UNIFIED CARD (image + content, one white block)
───────────────────────────────────────────── */
.pd-unified-card {
  background: #ffffff;
  border-radius: 16px;
  margin: 12px 12px 0;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05);
}

.pd-img-area {
  position: relative; // needed for badge absolute positioning
  width: 100%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 20px;
}

.pd-hero-img {
  width: 100%;
  max-width: 280px;
  height: auto;
  max-height: 260px;
  object-fit: contain;
  display: block;
}

.pd-card-divider {
  height: 1px;
  background: #f0f2f5;
  margin: 0;
}

.pd-content-card {
  padding: 20px 18px 24px;
  background: #ffffff;
}

/* ─────────────────────────────────────────────
   DESKTOP ZOOM — pure CSS checkbox hack, no JS
───────────────────────────────────────────── */
.pd-zoom-checkbox {
  display: none;
}

.pd-zoom-trigger {
  display: block;
  position: relative;
  cursor: zoom-in;
  width: 100%;

  .pd-zoom-icon {
    position: absolute;
    bottom: 10px;
    right: 10px;
    background: rgba(255,255,255,0.85);
    border-radius: 6px;
    padding: 4px 7px;
    font-size: 0.8rem;
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  }

  &:hover .pd-zoom-icon {
    opacity: 1;
  }
}

.pd-zoom-overlay {
  display: none;
  position: fixed;
  z-index: 9999;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(6px);
  align-items: center;
  justify-content: center;
  cursor: zoom-out;

  .pd-zoom-full-img {
    max-width: 85vw;
    max-height: 85vh;
    object-fit: contain;
    border-radius: 12px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.5);
    animation: pd-zoom-in 0.2s ease;
  }
}

.pd-zoom-checkbox:checked ~ .pd-zoom-overlay {
  display: flex;
}

@keyframes pd-zoom-in {
  from { transform: scale(0.92); opacity: 0; }
  to   { transform: scale(1);    opacity: 1; }
}

/* ─────────────────────────────────────────────
   MODAL STYLES
───────────────────────────────────────────── */
.pd-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0 8px;
}

.pd-modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $success;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pd-modal-check {
  color: $success;
}

.pd-modal-close {
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover { opacity: 1; }

  img { width: 18px; height: 18px; }
}

.pd-modal-body-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0;
}

.pd-modal-img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid #e8eaed;
  background: #ffffff;
  padding: 6px;
  flex-shrink: 0;
}

.pd-modal-info {
  flex: 1;
}

.pd-modal-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 6px;
  line-height: 1.4;
}

.pd-modal-price {
  font-size: 1rem;
  margin: 0;
  color: $primary;
  font-weight: 700;

  span {
    font-size: 0.75rem;
    color: #80868b;
    font-weight: 500;
    margin-left: 3px;
  }
}

/* ─────────────────────────────────────────────
   DEEP: override b-modal
───────────────────────────────────────────── */
:deep(.pd-modal .modal-footer) {
  flex-direction: column;
  padding: 16px;
  border-top: 1px solid #f0f2f5;
}

:deep(.pd-modal .modal-header) {
  padding-top: 10px !important;
  border-bottom: 1px solid;
  padding-bottom: 0 !important;
}

:deep(.pd-modal .modal-body) {
  padding: 16px 20px;
}
</style>